import React from "react";
import PageIntro from "../../../components/admin/PageIntro";
import SectionCard from "../../../components/admin/SectionCard";
import StatCard from "../../../components/data-display/StatCard";
import InfoCard from "../../../components/admin/InfoCard";
import { adminTheme } from "../../../components/admin/adminTheme";
import { useAuth } from "../../../hooks/useAuth";
import {
  useFinanceDashboard,
  useFinanceOfficerWorkbench,
} from "../hooks";
import type {
  FinanceDashboardResponseDto,
  FinanceOfficerWorkbenchResponseDto,
} from "../finance.types";

const FinanceDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const staffSubRole = user?.staffSubRole ?? null;

  const isHeadView = staffSubRole === "CEO" || staffSubRole === "FINANCE_HEAD";
  const isOfficerView = staffSubRole === "FINANCE_OFFICER";

  const headQuery = useFinanceDashboard({
    enabled: !isOfficerView,
  });
  const officerQuery = useFinanceOfficerWorkbench({
    enabled: isOfficerView,
  });

  if (isOfficerView) {
    const { data, isLoading, error } = officerQuery;

    if (isLoading) {
      return (
        <PageIntro
          title="Finance Officer Workbench"
          message="Loading assigned finance and refund tasks…"
        />
      );
    }

    if (error || !data) {
      return (
        <PageIntro
          title="Finance Officer Workbench"
          message="Unable to load finance data. Please try again."
        />
      );
    }

    const workbench: FinanceOfficerWorkbenchResponseDto = {
      cash: {
        pendingBookings: data.cash?.pendingBookings ?? 0,
        unresolvedAuditFlags: data.cash?.unresolvedAuditFlags ?? 0,
      },
      refunds: {
        pendingRequests: data.refunds?.pendingRequests ?? 0,
        highValueRequests: data.refunds?.highValueRequests ?? 0,
      },
      funding: {
        pendingTransfers: data.funding?.pendingTransfers ?? 0,
        reconciledTransfers: data.funding?.reconciledTransfers ?? 0,
      },
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: adminTheme.space.section,
          padding: adminTheme.space.section,
          background: adminTheme.color.pageBg,
          minHeight: "100%",
        }}
      >
        <PageIntro
          title="Finance Officer Workbench"
          message="Assigned revenue checks, refund handling, and cash reconciliation tasks."
        />

        <SectionCard title="Cash operations">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: adminTheme.space.lg,
            }}
          >
            <StatCard
              label="Pending bookings"
              value={workbench.cash.pendingBookings}
            />
            <StatCard
              label="Cash audit exceptions"
              value={workbench.cash.unresolvedAuditFlags}
            />
          </div>
        </SectionCard>

        <SectionCard title="Refund tasks">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: adminTheme.space.lg,
            }}
          >
            <StatCard
              label="Refund cases"
              value={workbench.refunds.pendingRequests}
            />
            <StatCard
              label="High-value requests"
              value={workbench.refunds.highValueRequests}
            />
          </div>
        </SectionCard>

        <SectionCard title="Funding operations">
          <InfoCard
            title="Assigned cases"
            body={`Pending transfers: ${workbench.funding.pendingTransfers}, Reconciled transfers: ${workbench.funding.reconciledTransfers}.`}
          />
        </SectionCard>
      </div>
    );
  }

  const { data, isLoading, error } = headQuery;

  if (isLoading) {
    return (
      <PageIntro
        title={isHeadView ? "Finance Head Dashboard" : "Finance Dashboard"}
        message="Loading finance overview…"
      />
    );
  }

  if (error || !data) {
    return (
      <PageIntro
        title={isHeadView ? "Finance Head Dashboard" : "Finance Dashboard"}
        message="Unable to load finance data. Please try again."
      />
    );
  }

  const dashboard: FinanceDashboardResponseDto = {
    kpis: {
      totalRevenue: data.kpis?.totalRevenue ?? "0",
      commissionRevenue: data.kpis?.commissionRevenue ?? "0",
      subscriptionRevenue: data.kpis?.subscriptionRevenue ?? "0",
      refundAmount: data.kpis?.refundAmount ?? "0",
      netRefundAfterRecovery: data.kpis?.netRefundAfterRecovery ?? "0",
      unresolvedCashAudits: data.kpis?.unresolvedCashAudits ?? 0,
    },
    revenue: {
      commission: data.revenue?.commission ?? "0",
      providerSubscriptions: data.revenue?.providerSubscriptions ?? "0",
      aiPlans: data.revenue?.aiPlans ?? "0",
      depositMargin: data.revenue?.depositMargin ?? "0",
    },
    refunds: {
      total: data.refunds?.total ?? "0",
      recovered: data.refunds?.recovered ?? "0",
      pendingRecovery: data.refunds?.pendingRecovery ?? "0",
    },
    cash: {
      mismatchedBookings: data.cash?.mismatchedBookings ?? 0,
      softAssumedPendingAudit: data.cash?.softAssumedPendingAudit ?? 0,
    },
  };

  const { kpis, revenue, refunds, cash } = dashboard;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: adminTheme.space.section,
        padding: adminTheme.space.section,
        background: adminTheme.color.pageBg,
        minHeight: "100%",
      }}
    >
      <PageIntro
        title={isHeadView ? "Finance Head Dashboard" : "Finance Dashboard"}
        message="Funds and funding management, revenue, refunds, and cash audit."
      />

      <SectionCard title="Finance KPIs">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: adminTheme.space.lg,
          }}
        >
          <StatCard
            label="Total revenue (month)"
            value={kpis.totalRevenue}
            helpText={`Commission: ${kpis.commissionRevenue}, Subscription: ${kpis.subscriptionRevenue}`}
          />
          <StatCard
            label="Refunds (month)"
            value={kpis.refundAmount}
            helpText={`Net of recovery: ${kpis.netRefundAfterRecovery}`}
          />
          <StatCard
            label="Unresolved cash audits"
            value={kpis.unresolvedCashAudits}
            helpText="Bookings with unresolved cash reconciliation"
          />
        </div>
      </SectionCard>

      <SectionCard title="Revenue breakdown">
        <InfoCard
          title="Revenue by source"
          body={`Commission on bookings: ${revenue.commission}, Provider subscriptions: ${revenue.providerSubscriptions}, AI plans: ${revenue.aiPlans}, Deposit margins: ${revenue.depositMargin}.`}
        />
      </SectionCard>

      <SectionCard title="Refunds & recovery">
        <InfoCard
          title="Refund flows"
          body={`Total refunds processed: ${refunds.total}, Recovered from providers: ${refunds.recovered}, Pending recovery: ${refunds.pendingRecovery}.`}
        />
      </SectionCard>

      <SectionCard title="Cash reconciliation & audit">
        <InfoCard
          title="Cash reconciliation exceptions"
          body={`Bookings with provider-customer mismatch: ${cash.mismatchedBookings}, Soft-assumed completions pending audit: ${cash.softAssumedPendingAudit}.`}
        />
      </SectionCard>
    </div>
  );
};

export default FinanceDashboardPage;